import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCoafCommunications, getStatusLabel, getStatusColor, getTypeLabel, getOperationTypeLabel } from '../../../hooks/useCoaf';
import { FileText, Search, Filter, Plus, Download, Eye, CheckCircle, Send } from 'lucide-react';

export default function CoafCommunicationList() {
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    startDate: '',
    endDate: '',
    search: '',
  });
  
  const { data, isLoading, error } = useCoafCommunications(filters);
  const communications = data?.data || data || [];

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="w-7 h-7 text-primary-400" />
            Comunicações COAF
          </h1>
          <p className="text-surface-400 mt-1">Lista de todas as comunicações ao COAF</p>
        </div>
        <Link
          to="/admin/compliance/coaf/new"
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 rounded-lg font-medium transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nova Comunicação
        </Link>
      </div>

      {/* Filters */}
      <div className="bg-surface-800 rounded-xl p-4 border border-surface-700">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-surface-400" />
            <input
              type="text"
              placeholder="Buscar..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          
          <select
            value={filters.status}
            onChange={(e) => handleFilterChange('status', e.target.value)}
            className="w-48 px-4 py-2 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Todos os Status</option>
            <option value="DRAFT">Rascunho</option>
            <option value="PENDING_APPROVAL">Aguardando Aprovação</option>
            <option value="APPROVED">Aprovada</option>
            <option value="REJECTED">Rejeitada</option>
            <option value="SENT">Enviada</option>
            <option value="CONFIRMED">Confirmada</option>
          </select>
          
          <select
            value={filters.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
            className="w-40 px-4 py-2 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Todos os Tipos</option>
            <option value="AUTOMATIC">Automática</option>
            <option value="MANUAL">Manual</option>
            <option value="COMPLEMENTARY">Complementar</option>
          </select>
          
          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
            className="w-40 px-4 py-2 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Data Início"
          />
          
          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
            className="w-40 px-4 py-2 bg-surface-700 border border-surface-600 rounded-lg focus:ring-2 focus:ring-primary-500"
            placeholder="Data Fim"
          />
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400">
          Erro ao carregar comunicações
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="bg-surface-800 rounded-xl p-8 border border-surface-700">
          <div className="animate-pulse space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-16 bg-surface-700 rounded"></div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      {!isLoading && (
        <div className="bg-surface-800 rounded-xl border border-surface-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-surface-400 text-sm bg-surface-700/50">
                  <th className="px-6 py-4 font-medium">ID</th>
                  <th className="px-6 py-4 font-medium">Tipo</th>
                  <th className="px-6 py-4 font-medium">Operação</th>
                  <th className="px-6 py-4 font-medium">Valor</th>
                  <th className="px-6 py-4 font-medium">Data Operação</th>
                  <th className="px-6 py-4 font-medium">Status</th>
                  <th className="px-6 py-4 font-medium">Criado em</th>
                  <th className="px-6 py-4 font-medium">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-700">
                {communications.map((comm: any) => (
                  <tr key={comm.id} className="hover:bg-surface-700/30 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-mono text-sm">{comm.id.slice(0, 8)}</span>
                      {comm.protocolNumber && (
                        <p className="text-xs text-surface-400 mt-1">
                          Protocolo: {comm.protocolNumber}
                        </p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm">{getTypeLabel(comm.type)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-sm">{getOperationTypeLabel(comm.operationType)}</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium">
                        {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(comm.operationValue)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-surface-300">
                      {new Date(comm.operationDate).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(comm.status)}`}>
                        {getStatusLabel(comm.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-surface-400">
                      {new Date(comm.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/admin/compliance/coaf/${comm.id}`}
                          className="p-2 hover:bg-surface-600 rounded-lg transition-colors"
                          title="Ver detalhes"
                        >
                          <Eye className="w-4 h-4" />
                        </Link>
                        {comm.status === 'APPROVED' && (
                          <button
                            className="p-2 hover:bg-green-500/20 rounded-lg transition-colors text-green-400"
                            title="Enviar"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        )}
                        {['SENT', 'CONFIRMED'].includes(comm.status) && (
                          <button
                            className="p-2 hover:bg-surface-600 rounded-lg transition-colors"
                            title="Exportar"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {communications.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-surface-400">
                      <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Nenhuma comunicação encontrada</p>
                      <Link
                        to="/admin/compliance/coaf/new"
                        className="text-primary-400 hover:text-primary-300 mt-2 inline-block"
                      >
                        Criar nova comunicação
                      </Link>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
